import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Text from 'shopper/components/Text';

import SkeletonGroup from 'components/Skeleton/SkeletonGroup';

import useAdBanner from 'hooks/useAdBanner';
import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { findAll } from 'lib/array';
import { isOffersFiltersActive } from 'lib/category';
import { isOfferFinished } from 'lib/offer';
import { noop } from 'lib/utils';

import OfferCard from './OfferCard/V1/OfferCardWithAd';

import OFFER from 'constants/offer';

const LoadMoreOffersButton = dynamic(() => import('./LoadMoreOffersButton'));
const NoOffersFoundForCategory = dynamic(
  () => import('./NoOffersFoundForCategory'),
  { ssr: false }
);
const NoOffersFoundForFilter = dynamic(
  () => import('./NoOffersFoundForFilter'),
  { ssr: false }
);
const OfferCardSkeleton = dynamic(
  () => import('./OfferCard/V1/OfferGridCard/Skeleton'),
  { ssr: false }
);

const OffersList = ({
  cardTitleAs = null,
  cardsAds = [],
  className,
  gaEventCategory = null,
  googleAds = [],
  endOfListMessage = null,
  initialOffers,
  offersFilters = null,
  paginationCappedAtFinishedOffer = false,
  showButtonBeforeFirstPagination = false,
  queryFn,
  queryKey,
  onOffersFiltersReset = noop,
  onLoadMoreOffersButtonClick = noop,
}) => {
  const router = useRouter();
  const fetchNextPageRef = useRef(null);
  const { isAdBannerVisible } = useAdBanner();
  const [isOfferWithFinishedStatusReached, setOfferWithFinishedStatusReached] =
    useState(false);
  const [isScrollPaginationEnabled, setScrollPaginationEnabled] = useState(
    !showButtonBeforeFirstPagination
  );

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isSuccess,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: (after, { signal }) => {
      if (!after) {
        setScrollPaginationEnabled(false);
      }

      return queryFn({ after }, { signal });
    },
    flattenKey: 'offers',
    initialData: initialOffers,
    refetchOnMount: !paginationCappedAtFinishedOffer,
    staleTime: 0,
    cacheTime: 0.5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    getNextPageParam: (lastOffers) => lastOffers?.after,
  });

  const visibleCardsAds = useMemo(() => {
    const isMagaluPage = router.asPath.includes('magalu');

    return isMagaluPage ? [] : cardsAds.filter(isAdBannerVisible);
  }, [cardsAds, router]);

  /**
   * Saves `cardAd` as a offer prop for perf as every `cardAd` can
   * be at any position at the list, so we would have to do double loops
   * every time this list is rendered otherwise
   */
  const { finishedOffers, offers } = useMemo(
    () => ({
      offers: data.map((offer, index) => ({
        ...offer,
        cardAds: findAll(
          visibleCardsAds,
          (cardAd) => cardAd.position - 1 === index
        ),
      })),
      finishedOffers: data.filter(isOfferFinished),
    }),
    [data, visibleCardsAds]
  );

  const { isNoOffersFoundForFilter, isOffersEmptyWithOffersFilterInactive } =
    useMemo(() => {
      const hasOffers = !isLoading && offers && offers.length > 0;
      const isCurrOffersFiltersActive = isOffersFiltersActive(offersFilters);

      return {
        isNoOffersFoundForFilter: !hasOffers && isCurrOffersFiltersActive,
        isOffersEmptyWithOffersFilterInactive:
          !hasOffers && !isCurrOffersFiltersActive,
      };
    }, [isLoading, offers, offersFilters]);

  useEffect(() => {
    const isLastOfferFinishedAndPaginationCappedAtFinishedOffer =
      paginationCappedAtFinishedOffer && finishedOffers.length > 0;

    if (isLastOfferFinishedAndPaginationCappedAtFinishedOffer) {
      setScrollPaginationEnabled(false);
    }

    setOfferWithFinishedStatusReached(
      isLastOfferFinishedAndPaginationCappedAtFinishedOffer
    );
  }, [finishedOffers, paginationCappedAtFinishedOffer]);

  useEffect(() => {
    setScrollPaginationEnabled(!showButtonBeforeFirstPagination);
  }, [showButtonBeforeFirstPagination]);

  useIntersectionObserver({
    enabled: hasNextPage && !isFetching && isScrollPaginationEnabled,
    target: fetchNextPageRef,
    rootMargin: '1200px',
    threshold: 0,
    onIntersect: fetchNextPage,
  });

  const onLoadMoreOffersButtonClickHandler = () => {
    onLoadMoreOffersButtonClick();
    fetchNextPage();
    setScrollPaginationEnabled(true);
  };

  const getActiveGoogleAdForCurrentOfferIndex = (index) =>
    googleAds.length > 0
      ? googleAds.find(({ position }) => position === index)
      : null;

  const showLoadMoreOffersButton =
    !isLoading &&
    !isScrollPaginationEnabled &&
    hasNextPage &&
    ((paginationCappedAtFinishedOffer && !isOfferWithFinishedStatusReached) ||
      !paginationCappedAtFinishedOffer);

  const showEndOfListMessage = !isLoading && !hasNextPage && endOfListMessage;

  return (
    <>
      {isSuccess && (
        <>
          {isNoOffersFoundForFilter && (
            <NoOffersFoundForFilter
              onOffersFiltersReset={onOffersFiltersReset}
            />
          )}
          {isOffersEmptyWithOffersFilterInactive && (
            <NoOffersFoundForCategory />
          )}
          {(offers.length > 0 ||
            finishedOffers.length > 0 ||
            !isNoOffersFoundForFilter) && (
            <>
              <div
                className={twMerge(
                  'mt-4 grid w-full flex-1 auto-rows-auto grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4',
                  !isScrollPaginationEnabled &&
                    isOfferWithFinishedStatusReached &&
                    'mb-10 md:mb-[60px]',
                  className
                )}
              >
                {offers.map((offer, index) => (
                  <OfferCard
                    /**
                     * API is currently sending duplicated `offer.key` for
                     * some reason. Quickfix this on our side for now.
                     */
                    key={`${offer.key}-${index}`}
                    activeGoogleAd={getActiveGoogleAdForCurrentOfferIndex(
                      index
                    )}
                    cardTitleAs={cardTitleAs}
                    gaEventCategory={gaEventCategory}
                    hasDropdownMenu={false}
                    offer={offer}
                    offersViewMode={OFFER.VIEW_MODES.GRID}
                  />
                ))}
              </div>
              {showLoadMoreOffersButton && (
                <LoadMoreOffersButton
                  onClick={onLoadMoreOffersButtonClickHandler}
                />
              )}
              {showEndOfListMessage && (
                <div className="mt-6 flex w-full justify-center">
                  <Text>{endOfListMessage}</Text>
                </div>
              )}
            </>
          )}
        </>
      )}
      {(isFetchingNextPage || isLoading) && (
        <div
          className={twMerge(
            'mb-10 mt-4 grid w-full grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4'
          )}
        >
          {<SkeletonGroup items={5} skeleton={OfferCardSkeleton} />}
        </div>
      )}
      <div ref={fetchNextPageRef} />
    </>
  );
};

OffersList.propTypes = {
  cardTitleAs: PropTypes.string,
  cardsAds: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      image: PropTypes.string,
      isClosable: PropTypes.bool,
      mobileImage: PropTypes.string,
      // Used only for hardcoded card ad and are relevant for SEO
      text: PropTypes.string,
      url: PropTypes.string,
      // Used only for hardcoded card ad
      onClick: PropTypes.func,
    })
  ),
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string,
  googleAds: PropTypes.arrayOf(
    PropTypes.shape({
      adId: PropTypes.string.isRequired,
      adPath: PropTypes.string.isRequired,
      position: PropTypes.number.isRequired,
      isMobileOnly: PropTypes.bool,
    })
  ),
  endOfListMessage: PropTypes.string,
  initialOffers: PropTypes.shape({
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sort: PropTypes.string,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }).isRequired,
  offersFilters: PropTypes.shape({
    priceRange: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    }),
    store: PropTypes.arrayOf(PropTypes.string),
    venue: PropTypes.string,
  }),
  paginationCappedAtFinishedOffer: PropTypes.bool,
  showButtonBeforeFirstPagination: PropTypes.bool,
  queryFn: PropTypes.func.isRequired,
  queryKey: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        storeId: PropTypes.number,
        storeName: PropTypes.string,
      }),
    ])
  ).isRequired,
  onOffersFiltersReset: PropTypes.func,
  onLoadMoreOffersButtonClick: PropTypes.func,
};

export default OffersList;
